import {
	Component,
	ElementRef,
	HostListener,
	Input,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	PdfModalComponent,
	PdfModalData,
} from '@components/pdf-modal/pdf-modal.component';
import { ICON } from '@core/util/icon';
import { MatterTitle } from '@models/matter';
import { Pin, SavedParcel, SavedPin } from '@models/teranet';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { SalePinActions, SalePinState } from '@state/sale_pins';
import { UserProfileState } from '@state/user-profile';
import { PurchaseStateType } from '@state/utils/models';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { CompareNamesModalComponent } from '../compare-names/modal/modal.component';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { openCloseWithState } from '@core/animations/animations';
import { TestHooks } from './pin-view.hooks';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { TeranetSyncDetailsComponent } from '../sync/sync.component';

@UntilDestroy()
@Component({
	selector: 'app-teranet-pin-view',
	templateUrl: './pin-view.component.html',
	styleUrls: ['./pin-view.component.scss'],
	animations: [openCloseWithState()],
	standalone: false,
})
export class PinViewComponent implements OnInit {
	testHooks = TestHooks;
	@Input() isOverview = false;
	@Input() showSimilarNames = false;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	@Select(SalePinState.getAllWithAdjacent) pins$: Observable<any[]>;
	@SelectSnapshot(SalePinState.getSelected) selectedPin: SavedPin;
	@SelectSnapshot(SalePinState.getParcelPurchaseState) parcelPurchaseState: {
		[pin: string]: { state: PurchaseStateType; error?: string };
	};
	@SelectSnapshot(SelectedMatter.id) saleId;
	@SelectSnapshot(SelectedMatter.matterNumber) matterNumber: string;

	@SelectSnapshot(UserProfileState.getTeranetPassword) teranetPassword: string;
	@Select(MatterTitlesState.getPrimaryTitle)
	primaryTitle$: Observable<MatterTitle>;

	@ViewChild('descriptionContent') descriptionContent!: ElementRef;
	@ViewChild('summaryContainer') summaryContainer!: ElementRef;

	public ICON = ICON;
	public pins;
	public purchaseState = PurchaseStateType;
	public purchaseDeleted = true;
	public primaryPin: string;
	public showDetails = true;
	public showFullDescription = false;
	public dynamicHeight = '55px';
	public expandable = false;
	public maxDescriptionHeight = 55;

	constructor(
		private dialog: MatDialog,
		private routeService: NavigationService,
		private drawerService: DrawerService,
	) {}

	@HostListener('window:resize')
	onResize(): void {
		this.checkAndApplyExpandable();
	}

	ngOnInit(): void {
		this.primaryTitle$.pipe(take(1)).subscribe((primaryTitle) => {
			if (primaryTitle) {
				this.primaryPin = primaryTitle.pinNumber;
			}
		});

		// set timeout to ensure the dom is fully loaded, less expensive than using afterViewChecked
		setTimeout(() => {
			this.checkAndApplyExpandable();
		}, 0);
	}

	@Dispatch()
	_purchaseMap(pin: SavedPin) {
		return new SalePinActions.PurchaseAdjacentMap(pin);
	}

	@Dispatch()
	_purchaseParcel(pin: Pin, purchase_deleted: boolean, parent: boolean) {
		return new SalePinActions.PurchaseParcel({
			pin,
			include_deleted: purchase_deleted,
			parent,
		});
	}

	@Dispatch()
	_selectPin(pin: string) {
		return new SalePinActions.SelectPin(pin);
	}

	purchaseParcel() {
		this._purchaseParcel(
			this.selectedPin,
			this.purchaseDeleted,
			this.selectedPin.parent,
		);
	}

	selectPin(pin: SavedPin) {
		this._selectPin(pin.pin);
	}

	openPDF = (parcel: SavedParcel) => {
		const fileName = `PIN_${this.selectedPin?.pin}`.replace(/\./g, '');
		const data: PdfModalData = {
			googleSrcUrl: parcel.uploaded_document_path,
			fileName,
		};
		this.drawerService.openDrawer(PdfModalComponent, {
			data,
			size: 'lg',
			title: 'PIN ' + this.selectedPin?.pin,
			noStyle: true,
		});
	};

	purchaseMap = () => {
		this._purchaseMap(this.selectedPin);
		this.routeService.goToMatter_TitleSearch_Map(this.saleId);
	};

	viewMap() {
		this.routeService.goToMatter_TitleSearch_Map(this.saleId);
	}

	goToSync() {
		this.drawerService.openDrawer(TeranetSyncDetailsComponent, {
			title: 'Sync Details',
			size: 'lg',
		});
	}

	goToCompare() {
		this.dialog.open(
			CompareNamesModalComponent,
			CompareNamesModalComponent.defaultDialogConfig,
		);
	}

	setPurchaseDeleted(purchaseDeleted: boolean) {
		this.purchaseDeleted = purchaseDeleted;
	}

	toggleShowDetails() {
		this.showDetails = !this.showDetails;
	}

	checkAndApplyExpandable() {
		const content = this.descriptionContent.nativeElement;

		if (content) {
			const contentHeight = content.scrollHeight;

			if (contentHeight > this.maxDescriptionHeight) {
				this.expandable = true;
			} else {
				this.expandable = false;
			}
		}
	}

	toggleShowFullDescription() {
		this.showFullDescription = !this.showFullDescription;

		if (this.showFullDescription) {
			this.dynamicHeight =
				this.descriptionContent.nativeElement.scrollHeight + 'px';
		} else {
			this.dynamicHeight = this.maxDescriptionHeight + 'px';
		}
	}
}
